// internal/AgentsPage.jsx — Centre des Agents IA (§47-48) : périmètres + niveaux d'autonomie réglables par le CEO.
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import InternalLayout, { S, IT } from "@/internal/InternalLayout";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api/internal`;
const H = () => ({ "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("civ_user_token") || ""}` });
const LEVELS = {
  1: { l: "Suggestion uniquement", c: "#8BA394" },
  2: { l: "Préparation automatique", c: "#38BDF8" },
  3: { l: "Exécution après validation", c: "#2EE27B" },
  4: { l: "Auto sur actions réversibles", c: "#F59E0B" },
  5: { l: "Critique = validation CEO", c: "#F87171" },
};
const WS_COLOR = { ceo: "#E8B84B", tech: "#38BDF8", product: "#A78BFA", community: "#FB923C", field: "#F59E0B" };

export default function AgentsPage() {
  const [agents, setAgents] = useState(null);
  const [denied, setDenied] = useState(false);
  const load = useCallback(() => {
    fetch(`${API}/agents`, { headers: H() }).then((r) => r.json()).then((d) => (d.ok ? setAgents(d.agents) : setDenied(true))).catch(() => setDenied(true));
  }, []);
  useEffect(() => { load(); }, [load]);

  const setAutonomy = async (key, autonomy) => {
    const r = await fetch(`${API}/agents/${key}`, { method: "PATCH", headers: H(), body: JSON.stringify({ autonomy }) });
    const d = await r.json();
    if (d.ok) { toast.success(`Autonomie niveau ${autonomy} — ${LEVELS[autonomy].l}`); load(); }
    else toast.error(d.error);
  };

  return (
    <InternalLayout title="Agents IA" subtitle="Chaque agent a un périmètre clair et un niveau d'autonomie que vous contrôlez. Les actions critiques exigent toujours une validation humaine.">
      {denied && <div style={{ ...S.card, padding: 18, color: "#F87171", fontSize: 13 }} data-testid="agents-denied">Accès réservé au CEO.</div>}
      {!agents && !denied && <div style={{ color: IT.muted, fontSize: 13 }}>Chargement…</div>}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(310px, 1fr))", gap: 12 }} data-testid="agents-grid">
        {(agents || []).map((a) => {
          const col = WS_COLOR[a.workspace] || IT.green;
          return (
            <div key={a.agent_key} style={{ ...S.card, padding: 16 }} data-testid={`agent-${a.agent_key}`}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ width: 38, height: 38, borderRadius: 11, background: `${col}18`, border: `1px solid ${col}55`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 17 }}>🤖</span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: "block", fontWeight: 900, fontSize: 14.5 }}>{a.name}</span>
                  <span style={{ fontSize: 10.5, color: IT.muted }}>Workspace : {a.workspace}</span>
                </span>
                <span style={{ width: 8, height: 8, borderRadius: 99, background: IT.green, boxShadow: `0 0 8px ${IT.green}` }} title="Actif" />
              </div>
              <div style={{ fontSize: 12, color: IT.text, margin: "11px 0 7px", fontWeight: 700 }}>🎯 {a.scope}</div>
              <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                {(a.capabilities || []).map((cp) => (
                  <span key={cp} style={{ fontSize: 9.5, fontWeight: 700, color: IT.muted, border: "1px solid rgba(139,163,148,0.28)", borderRadius: 999, padding: "2px 8px" }}>{cp}</span>
                ))}
              </div>
              <div style={{ margin: "13px 0 6px", fontSize: 10.5, fontWeight: 900, color: IT.muted, letterSpacing: 0.4 }}>
                NIVEAU D'AUTONOMIE — <span style={{ color: LEVELS[a.autonomy]?.c }}>{a.autonomy} · {LEVELS[a.autonomy]?.l}</span>
              </div>
              <div style={{ display: "flex", gap: 5 }} data-testid={`agent-autonomy-${a.agent_key}`}>
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} data-testid={`agent-${a.agent_key}-lvl-${n}`} onClick={() => setAutonomy(a.agent_key, n)} title={LEVELS[n].l}
                    style={{ flex: 1, padding: "7px 0", borderRadius: 9, fontSize: 12, fontWeight: 900, cursor: "pointer", fontFamily: "inherit",
                      border: `1px solid ${a.autonomy === n ? LEVELS[n].c : "rgba(139,163,148,0.25)"}`,
                      background: a.autonomy === n ? `${LEVELS[n].c}1e` : "rgba(255,255,255,0.02)",
                      color: a.autonomy === n ? LEVELS[n].c : IT.muted }}>{n}</button>
                ))}
              </div>
              {(a.recent_actions || []).length > 0 && (
                <div style={{ marginTop: 12, borderTop: "1px solid rgba(255,255,255,0.06)", paddingTop: 8 }}>
                  {a.recent_actions.map((act, i) => (
                    <div key={i} style={{ fontSize: 10.5, color: IT.muted, padding: "2px 0" }}>
                      <span style={{ color: IT.green }}>{act.action}</span> · {act.by} · {new Date(act.ts).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </InternalLayout>
  );
}
